import React from "react";
import { i18n } from "@lingui/core";
import { Plural, Trans } from "@lingui/macro";
import "./cruise-card.scss";
import { Button, ImageRatio } from "@silversea-ssc/ui-components-react";
import { DestinationColor } from "@silversea-ssc/ui-components-react";
import { parseISODateOnlyToLocal } from "src/utils/date";
import ErrorBoundary from "components/ErrorBoundary";
import { AlgoliaCruiseHitImage } from "types/algolia";
import { Icon } from "@silversea-ssc/ui-components-react";
import { ResponsiveSanityImage } from "@silversea-ssc/ui-components-react";

export const cruiseCardImageWidth = 300;

export interface ItineraryItemModel {
  date: Date;
  city: string;
  cityCode: string;
  country: string;
  region: string;
  worldLocation: string;
}

export interface Port {
  name: string;
  portCode: string;
}

export interface TerminalPort extends Port {
  worldLocation: string;
}

export interface CruiseCardModel {
  voyageCode: string;
  image: AlgoliaCruiseHitImage;
  priceFrom?: string | null;
  departureDate: string;
  shipName: string;
  passengerCount: number;
  durationInDays: number;
  starText?: string;
  departurePort: TerminalPort;
  arrivalPort: TerminalPort;
  available: boolean;
  detailUrl: string;
  destination?: string;
  specialType?: string;
  destinationId?: number | string;
  destinationName?: string;
  isExpedition?: boolean;
  showDestination?: boolean;
}

type Props = CruiseCardModel;

const CruiseCard: React.FC<Props> = (props) => {
  return (
    <DestinationColor
      destinationId={props.destinationId}
      key={props.voyageCode}
      className={`ssc-cruise-card ${
        props.starText ? "ssc-cruise-card--top-cruise" : ""
      }`}
    >
      <div className="ssc-cruise-card__container">
        <div className="ssc-cruise-card__header">
          {props.showDestination && (
            <h4 className="ssc-cruise-card__destination">
              {props.destinationName}
            </h4>
          )}
          <div className="ssc-cruise-card__image-container">
            {props.image && (
              <ImageRatio ratio="card-compact">
                <ResponsiveSanityImage
                  image={props.image}
                  alt={`Cruise ${props.voyageCode}`}
                  defaultWidth={260}
                  defaultHeight={150}
                />
              </ImageRatio>
            )}

            {props.starText && (
              <div className="ssc-cruise-card__image__hilights">
                <Icon
                  padding="small"
                  size={props.specialType == "AntarcticaBridge" ? "1.5x" : "1x"} //TODO SOLVE ICON SIZE
                  name={
                    props.specialType == "AntarcticaBridge"
                      ? "antarctica-bridge"
                      : "star"
                  }
                />
                <span className="ssc-cruise-card__image__hilights-label">
                  {props.starText}
                </span>
              </div>
            )}

            {!props.starText && props.isExpedition && (
              <div className="ssc-cruise-card__image__hilights">
                <span className="ssc-cruise-card__image__hilights-label">
                  Expedition Cruise
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="ssc-cruise-card__content">
          <div className="ssc-cruise-card__endpoint-container">
            <div className="ssc-cruise-card__endpoint-label">
              <Trans id="Departure (abbr)">Dep</Trans>
            </div>
            <div className="ssc-cruise-card__endpoint">
              <div className="ssc-cruise-card__endpoint-date">
                {props.departureDate && (
                  <ErrorBoundary>
                    {i18n.date(parseISODateOnlyToLocal(props.departureDate), {
                      year: "numeric",
                      month: "short",
                      day: "numeric"
                    })}
                  </ErrorBoundary>
                )}
              </div>
              <h3 className="ssc-cruise-card__endpoint-name">
                {props.departurePort.name}
              </h3>
              <p className="ssc-cruise-card__endpoint-description">
                {props.departurePort.worldLocation}
              </p>
            </div>
          </div>
          <div className="ssc-cruise-card__endpoint-container">
            <div className="ssc-cruise-card__endpoint-label">
              <Trans id="Arrival (abbr)">Arr</Trans>
            </div>
            <div className="ssc-cruise-card__endpoint">
              <h3 className="ssc-cruise-card__endpoint-name">
                {props.arrivalPort.name}
              </h3>
              <p className="ssc-cruise-card__endpoint-description">
                {props.arrivalPort.worldLocation}
              </p>
            </div>
          </div>
          <div className="ssc-cruise-card__trip">
            <div className="ssc-cruise-card__trip-container">
              <div className="ssc-cruise-card__trip-duration">
                <Plural
                  value={props.durationInDays || 0}
                  one="# DAY"
                  other="# DAYS"
                />
              </div>
              {props.available && props.priceFrom != null && (
                <div className="ssc-cruise-card__trip-price">
                  <span className="ssc-cruise-card__trip-price__from-label">
                    <Trans id="price-from">FROM</Trans>
                  </span>
                  {props.priceFrom}
                </div>
              )}
              {(!props.available || !props.priceFrom) && (
                <div className="ssc-cruise-card__trip-price--waitlist">
                  <Trans id="waitlist (price)">WAITLIST</Trans>
                </div>
              )}
            </div>
          </div>

          <div className="ssc-cruise-card__ship-container">
            <Icon
              name="ship"
              padding={false}
              className="ssc-cruise-card__ship-icon"
            />
            <div className="ssc-cruise-card__ship-info">
              <h3 className="ssc-cruise-card__ship-title">{props.shipName}</h3>
              <h3 className="ssc-cruise-card__ship-counter">
                <Plural
                  value={props.passengerCount}
                  one="# Guest"
                  other="# Guests"
                />
              </h3>
            </div>
            <div className="ssc-cruise-card__ship-code">
              #{props.voyageCode}
            </div>
          </div>
        </div>
        <div className="ssc-cruise-card__footer">
          <div className="ssc-cruise-card__buttons">
            <Button
              full
              variant="primary"
              iconName="arrow-right"
              href={props.detailUrl}
              data-content-piece="cruise_details"
              data-content-params={{
                departurePort: props.departurePort.portCode,
                arrivalPort: props.arrivalPort.portCode
              }}
            >
              <Trans id="cruis card details button label">details</Trans>
            </Button>
          </div>
        </div>
      </div>
    </DestinationColor>
  );
};

export default CruiseCard;
